import React from 'react';
import './Work.css';
import { CASE_STUDIES, type CaseStudy } from '../content';
import { ClipFigure, ClipGrid, MediaBand, StillFigure } from '../components/Media';

/** Renders **bold** spans so body copy can emphasise a phrase without HTML. */
function RichText({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
}

/** Keeps tiles a sensible size: a lone clip should not span the whole band,
 *  and eight of them should not each be a thumbnail. */
function columnsFor(count: number): 1 | 2 | 3 | 4 {
  if (count <= 1) return 2;
  if (count >= 4) return 4;
  return 3;
}

function Study({ study }: { study: CaseStudy }) {
  return (
    <article className="study" id={study.id}>
      <div className="wrap">
        <header className="study-head">
          <span className="study-index">{study.index}</span>
          <div className="study-titles">
            <h3 className="study-title">{study.title}</h3>
            <p className="study-subtitle">{study.subtitle}</p>
          </div>
        </header>

        <dl className="study-meta">
          {study.meta.map(({ label, value }) => (
            <div className="study-meta-row" key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        <p className="study-lede">{study.lede}</p>
      </div>

      <div className="wrap wrap--wide study-feature">
        <ClipFigure clip={study.feature} />
      </div>

      <div className="wrap">
        <div className="study-body measure">
          {study.body.map((para, i) => (
            <p className="body-text" key={i}>
              <RichText text={para} />
            </p>
          ))}
        </div>

        {study.features && (
          <div className="study-features measure">
            <h4 className="study-features-heading">{study.features.heading}</h4>
            <ul className="feature-list">
              {study.features.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {study.clips.length > 0 && (
        <div className="wrap wrap--wide study-gallery">
          <ClipGrid clips={study.clips} cols={columnsFor(study.clips.length)} />
        </div>
      )}

      {study.band && (
        <MediaBand
          media={{ kind: 'image', src: study.band.src, alt: study.band.alt }}
          variant="inset"
          className="study-band"
        >
          <p className="eyebrow eyebrow--accent">{study.band.eyebrow}</p>
          <p className="band-title">{study.band.title}</p>
          <p className="band-text">{study.band.text}</p>
        </MediaBand>
      )}

      {study.stills && (
        <div className="wrap wrap--wide study-gallery">
          <div className={`study-stills study-stills--${study.stills.length}`}>
            {study.stills.map((still) => (
              <StillFigure
                key={still.src}
                {...still}
                ratio={still.ratio ?? (study.stills!.length > 1 ? '1/1' : '16/9')}
              />
            ))}
          </div>
        </div>
      )}

      {study.diagrams && (
        <div className="wrap study-diagrams">
          <p className="study-diagram-note">
            I produced the technical illustration and documentation, including a write-up past
            120 pages. A later senior design team picked the project up; I advised them.
          </p>
          <div className="study-diagram-grid">
            {study.diagrams.map((d) => (
              <figure className="study-diagram" key={d.src}>
                <img src={d.src} alt={d.alt} loading="lazy" />
                <figcaption>{d.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export default function Work() {
  return (
    <section id="work" className="work section-pad">
      <div className="wrap work-head">
        <p className="eyebrow eyebrow--accent">Selected work</p>
      </div>

      {CASE_STUDIES.map((study) => (
        <Study key={study.id} study={study} />
      ))}
    </section>
  );
}
